import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Globe, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { currentLanguage, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: t('home'), href: '#home' },
    { name: t('languages'), href: '#languages' },
    { name: t('courses'), href: '#courses' },
    { name: t('about'), href: '#about' },
    //{/*{ name: t('instructors'), href: '#instructors' }, */},
    { name: t('contact'), href: '#contact' },
  ];

  return (
    <>

      {/* Main Navigation */}
      <motion.header
        className={`fixed w-full z-50 top-0 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <nav className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <div className="w-12 h-12 bg-gradient-accent rounded-2xl flex items-center justify-center">
                <img src={process.env.PUBLIC_URL + '/Final.png'} alt="Logo" className="w-8 h-8" />
              </div>
              <div>
                <h1 className={`text-xl font-bold font-heading ${
                  isScrolled ? 'text-primary-500' : 'text-white'
                }`}>
                  YorkSchool
                </h1>
                <p className={`text-xs ${
                  isScrolled ? 'text-neutral-600' : 'text-white/80'
                }`}>
                  Language School
                </p>
              </div>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  className={`font-medium transition-colors hover:text-accent-500 ${
                    isScrolled ? 'text-primary-500' : 'text-white'
                  }`}
                  whileHover={{ y: -2 }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {item.name}
                </motion.a>
              ))}
            </div>

            {/* Language Switcher & CTA Button */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Language Toggle Button */}
              <motion.button
                onClick={toggleLanguage}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl border-2 transition-all duration-300 ${
                  isScrolled 
                    ? 'border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white' 
                    : 'border-white/30 text-white hover:bg-white/20'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Globe className="w-4 h-4" />
                <span className="font-medium text-sm">
                  {currentLanguage === 'en' ? 'PT' : 'EN'}
                </span>
              </motion.button>

              <motion.a
                href="#contact"
                className="btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('bookFreeTrial')}
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              className={`lg:hidden p-2 ${
                isScrolled ? 'text-primary-500' : 'text-white'
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileTap={{ scale: 0.95 }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <motion.div
          className={`lg:hidden bg-white border-t ${
            isMenuOpen ? 'block' : 'hidden'
          }`}
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isMenuOpen ? 1 : 0,
            height: isMenuOpen ? 'auto' : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-4 py-6 space-y-4">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="block text-primary-500 font-medium hover:text-accent-500 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item.name}
              </a>
            ))}
            
            {/* Mobile Language Switcher */}
            <motion.button
              onClick={toggleLanguage}
              className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white rounded-xl transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Globe className="w-5 h-5" />
              <span className="font-medium">
                {currentLanguage === 'en' ? 'Português' : 'English'}
              </span>
            </motion.button>
            
            <a
              href="#contact"
              onClick={() => setIsMenuOpen(false)}
              className="w-full btn-primary mt-4"
            >
              {t('bookFreeTrial')}
            </a>
          </div>
        </motion.div>
      </motion.header>
    </>
  );
};

export default Header;
